import { action, computed, observable, store } from '~/common/mobx.decorator'
import flatten from 'lodash/flatten'
import { persist } from 'mobx-persist'

@store({ persist: true })
class GenericTableStore {
	@observable sortField
	@observable sortOrder

	@observable innerSortField
	@observable innerSortOrder

	@persist('object') @observable appliedFilters = {} // Structure: { [tableName]: { [filterKey]: [filterItems] } }
	@observable popOverHeight = 0

	@persist('object') @observable recentFilters = {}

	@computed
	get hasAppliedFilters() {
		return Object.keys(this.appliedFilters).length > 0
	}

	@computed
	get allFilters() {
		return flatten(Object.values(this.appliedFilters))
	}

	@computed
	get allRecentFilters() {
		return flatten(Object.values(this.recentFilters))
	}

	@computed
	get allFiltersValue() {
		// Get filter items for query param
		return Object.keys(this.appliedFilters)?.reduce((accumulative, currentKey) => {
			accumulative[currentKey] = this.appliedFilters[currentKey]?.map((item) => item.value)

			return accumulative
		}, {})
	}

	// Get filters for a specific table
	getFiltersForTable = (tableName) => {
		if (!tableName) return {}
		return this.appliedFilters[tableName] || {}
	}

	// Get filter values for a specific table
	getFiltersValueForTable = (tableName) => {
		if (!tableName) return {}
		const tableFilters = this.getFiltersForTable(tableName)
		return Object.keys(tableFilters)?.reduce((accumulative, currentKey) => {
			accumulative[currentKey] = tableFilters[currentKey]?.map((item) => item.value)
			return accumulative
		}, {})
	}

	// Check if a table has applied filters
	hasAppliedFiltersForTable = (tableName) => {
		if (!tableName) return false
		const tableFilters = this.getFiltersForTable(tableName)
		return Object.keys(tableFilters).length > 0
	}

	@action
	addRecentFilter = (filterKey, filterItem) => {
		const existedFilterItem = this.recentFilters[filterKey]?.find((item) => item.value === filterItem.value)
		if (existedFilterItem?.value === filterItem.value) {
			return
		}

		if (this.recentFilters[filterKey]?.length >= 5) {
			const [firstElements] = this.recentFilters[filterKey]

			Object.keys(this.recentFilters).forEach((recentFilterKey) => {
				const matchedFilterItem = this.recentFilters[recentFilterKey]?.find((item) => item.value === firstElements.value)

				if (matchedFilterItem) {
					this.recentFilters[recentFilterKey] = this.recentFilters[recentFilterKey].filter((filter) => filter.value !== matchedFilterItem.value)
				}
			})
		}

		this.recentFilters[filterKey] = [...(this.recentFilters[filterKey] ?? []), filterItem]
	}

	@action
	addFilter = (filterKey, filterItem, tableName) => {
		if (!tableName) {
			// Fallback to old behavior for backward compatibility
			this.appliedFilters[filterKey] = [...(this.appliedFilters[filterKey] ?? []), filterItem]
			this.addRecentFilter(filterKey, filterItem)
			return
		}

		if (!this.appliedFilters[tableName]) {
			this.appliedFilters[tableName] = {}
		}

		this.appliedFilters[tableName][filterKey] = [...(this.appliedFilters[tableName][filterKey] ?? []), filterItem]
		this.addRecentFilter(filterKey, filterItem)
	}

	@action
	removeFilter = (filterKey, filterValue, tableName) => {
		if (!tableName) {
			// Fallback to old behavior for backward compatibility
			if (!this.appliedFilters[filterKey]) {
				return
			}
			this.appliedFilters[filterKey] = this.appliedFilters[filterKey].filter((filter) => filter.value !== filterValue)
			if (this.appliedFilters[filterKey]?.length > 0) {
				return
			}
			delete this.appliedFilters[filterKey]
			return
		}

		if (!this.appliedFilters[tableName] || !this.appliedFilters[tableName][filterKey]) {
			return
		}

		this.appliedFilters[tableName][filterKey] = this.appliedFilters[tableName][filterKey].filter((filter) => filter.value !== filterValue)

		if (this.appliedFilters[tableName][filterKey]?.length > 0) {
			return
		}

		delete this.appliedFilters[tableName][filterKey]

		// Clean up empty table entry
		if (Object.keys(this.appliedFilters[tableName]).length === 0) {
			delete this.appliedFilters[tableName]
		}
	}

	@action
	clearAllFilters = (tableName) => {
		if (!tableName) {
			// Fallback to old behavior for backward compatibility
			this.appliedFilters = {}
			return
		}

		if (this.appliedFilters[tableName]) {
			delete this.appliedFilters[tableName]
		}
	}

	@action
	clearAllSortFilter = () => {
		this.appliedFilters = {}
		this.sortField = undefined
		this.sortOrder = undefined
	}

	@action
	onUpdateSortField = (sortField) => {
		if (sortField !== this.sortField) {
			this.sortField = sortField
		}
	}

	@action
	onUpdateSortOrder = (sortOrder) => {
		if (sortOrder !== this.sortOrder) {
			this.sortOrder = sortOrder
		}
	}

	@action
	onUpdateSort = (sortField, sortOrder) => {
		this.onUpdateSortField(sortField)
		this.onUpdateSortOrder(sortOrder)
	}

	@action
	onUpdateInnerSortField = (innerSortField) => {
		if (innerSortField !== this.innerSortField) {
			this.innerSortField = innerSortField
		}
	}

	@action
	onUpdateInnerSortOrder = (innerSortOrder) => {
		if (innerSortOrder !== this.innerSortOrder) {
			this.innerSortOrder = innerSortOrder
		}
	}

	@action
	onUpdateInnerSort = (innerSortField, innerSortOrder) => {
		this.onUpdateInnerSortField(innerSortField)
		this.onUpdateInnerSortOrder(innerSortOrder)
	}

	@action
	setDefaultSort = (sortField, sortOrder) => {
		if (!this.sortField && !this.sortOrder) {
			this.sortField = sortField
			this.sortOrder = sortOrder
		}
	}

	@action
	setDefaultInnerSort = (innerSortField, innerSortOrder) => {
		if (!this.innerSortField && !this.innerSortOrder) {
			this.innerSortField = innerSortField
			this.innerSortOrder = innerSortOrder
		}
	}

	@action
	updatePopoverHeight = (height) => {
		this.popOverHeight = height
	}
}

export const genericTableStore = new GenericTableStore()
